import { useCallback, useEffect } from 'react'
import { F0AuraVoiceAnimation, F0Button, useAiChat } from '@factorialco/f0-react'
import {
  RoomAudioRenderer,
  SessionProvider,
  useRoomContext,
  useSession,
  useVoiceAssistant,
} from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'

import i18n from 'modules/core/lib/i18n'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { getCursor, removeCursor } from 'modules/ai_voice/lib/cursor'
import { disposeAiMask, initAiMask, setMaskStyle, showAiMask } from 'modules/ai_voice/lib/mask'
import { registerRouteObserver, unregisterRouteObserver } from 'modules/ai_voice/lib/route-observer'
import { registerRpcMethods, unregisterRpcMethods } from 'modules/ai_voice/lib/rpc'
import { useVoiceSession } from 'modules/ai_voice/providers/voiceSession'
import { useVoiceSessionTokenSource } from 'modules/ai_voice/hooks/useVoiceSessionTokenSource'

function RoomContext() {
  const room = useRoomContext()
  const { chatWidth } = useAiChat()

  useEffect(() => {
    initAiMask()
    showAiMask()
    getCursor()
    registerRpcMethods(room)
    registerRouteObserver(room)
    return () => {
      disposeAiMask()
      removeCursor()
      unregisterRpcMethods(room)
      unregisterRouteObserver()
    }
  }, [room])

  useEffect(() => {
    setMaskStyle({ right: `${chatWidth}px` })
  }, [chatWidth])

  return null
}

function VoiceAnimationConnected() {
  const { audioTrack, state } = useVoiceAssistant()

  return <F0AuraVoiceAnimation state={state} audioTrack={audioTrack} />
}

interface VoiceClientProps {
  agent: string
  task: string
}

export function VoiceClient({ agent, task }: VoiceClientProps) {
  const { company, employee, canCreateEmployees } = useCurrentSession()
  const participantAttributes = {
    agent,
    task,
    url: window.location.href,
    locale: i18n.locale,
    firstName: employee.firstName,
    lastName: employee.lastName,
    userId: employee.id,
    isAdmin: canCreateEmployees,
    companyName: company.name,
    companyId: company.id,
  }
  const tokenSource = useVoiceSessionTokenSource(participantAttributes)
  const session = useSession(tokenSource)
  const { registerControls } = useVoiceSession()

  const handleConnect = useCallback(async () => {
    await session.start()
  }, [session])

  const handleDisconnect = useCallback(async () => {
    await session.end()
  }, [session])

  useEffect(() => {
    return registerControls({ start: handleConnect, stop: handleDisconnect })
  }, [registerControls, handleConnect, handleDisconnect])

  const isConnected = session.isConnected
  const isConnecting = session.connectionState === ConnectionState.Connecting

  return (
    <SessionProvider session={session}>
      {!isConnected && (
        <>
          <div className="mx-auto my-auto">
            <F0AuraVoiceAnimation state={isConnecting ? 'connecting' : 'disconnected'} />
          </div>
          <div className="mx-auto mb-16">
            <F0Button
              onClick={handleConnect}
              disabled={isConnecting}
              label={isConnecting ? 'Connecting...' : 'Start'}
              variant="default"
            />
          </div>
        </>
      )}

      {isConnected && (
        <>
          <RoomContext />
          <RoomAudioRenderer />
          <div className="mx-auto my-auto">
            <VoiceAnimationConnected />
          </div>
          <div className="mx-auto mb-16">
            <F0Button onClick={handleDisconnect} label="End" variant="neutral" />
          </div>
        </>
      )}
    </SessionProvider>
  )
}
